import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import PlayerActions from 'reducers/Player'
import {
  thunks as PlayQueueThunks,
  getNextItemAndSong
} from 'reducers/PlayQueue'
import { trackEvent } from 'utils/segmentService'
import { normalizeValue } from 'utils/analytics'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'

const mapStateToProps = ({ player, playQueue }) => {
  const currentSong = player.song || {}
  const {
    isPlaying,
    seekTime,
    currentTime,
    isLoading,
    volume = 50,
    autoPlay
  } = player
  const { mediaURL } = currentSong
  return {
    isPlaying,
    seekTime,
    currentTime,
    isLoading,
    mediaURL,
    playQueue,
    volume,
    autoPlay,
    song: currentSong
  }
}

@connect(mapStateToProps)
export default class Audio extends React.PureComponent {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    playQueue: PropTypes.object.isRequired,
    song: PropTypes.object,
    isPlaying: PropTypes.bool,
    seekTime: PropTypes.number,
    currentTime: PropTypes.number,
    isLoading: PropTypes.bool,
    mediaURL: PropTypes.string,
    volume: PropTypes.number
  }

  static defaultProps = {
    isPlaying: false,
    seekTime: null,
    mediaURL: null,
    currentTime: 0,
    isLoading: false,
    volume: 50,
    song: null
  }

  constructor(props) {
    super(props)
    this.player = null
    this.seekCount = 0
    this.lastPosition = 0
    this.setRef = (element) => {
      if (element) {
        this.player = element
        this.updatePlayerSource()
        this.player.volume = this.props.volume / 100
      }
    }
  }

  componentDidMount() {
    if (this.player) {
      this.player.addEventListener('timeupdate', this.timeUpdate)
      this.player.addEventListener('play', this.play)
      this.player.addEventListener('pause', this.pause)
      this.player.addEventListener('ended', this.ended)
      this.player.addEventListener('loadeddata', this.loadedData)
      this.player.addEventListener('durationchange', this.durationChange)
    }
  }

  componentDidUpdate(prevProps) {
    const { isPlaying, seekTime, mediaURL, dispatch, volume, song } = this.props

    if (mediaURL !== prevProps.mediaURL) {
      const stopPosition =
        (this.player && this.player.currentTime) ||
        this.lastPosition ||
        prevProps.currentTime ||
        0
      this.logPlaybackStop('track_change', prevProps.song, stopPosition)
      this.seekCount = 0
      this.lastPosition = 0
      this.updatePlayerSource()
    }

    if (isPlaying !== prevProps.isPlaying) {
      this.handlePlayPause()
    }

    if (seekTime !== prevProps.seekTime && seekTime !== null) {
      this.seekCount += 1
      trackEvent('Playback Seek', {
        id: normalizeValue(song?.trackid || song?.id),
        artist: normalizeValue(song?.artist),
        title: normalizeValue(song?.title),
        from_position: normalizeValue(prevProps.currentTime),
        to_position: normalizeValue(seekTime),
        seek_count: normalizeValue(this.seekCount),
        source: 'player',
        type: 'seek'
      })
      this.player.currentTime = seekTime
      dispatch(PlayerActions.seekTimeFinished())
    }

    if (volume !== prevProps.volume) {
      this.player.volume = volume / 100
    }
  }

  componentWillUnmount() {
    if (this.player) {
      this.player.removeEventListener('timeupdate', this.timeUpdate)
      this.player.removeEventListener('play', this.play)
      this.player.removeEventListener('pause', this.pause)
      this.player.removeEventListener('ended', this.ended)
      this.player.removeEventListener('loadeddata', this.loadedData)
      this.player.removeEventListener('durationchange', this.durationChange)
    }
    const stopPosition =
      (this.player && this.player.currentTime) || this.lastPosition || 0
    this.logPlaybackStop('unmount', this.props.song, stopPosition)
  }

  updatePlayerSource = () => {
    const { mediaURL, isPlaying } = this.props

    if (this.player && mediaURL) {
      this.player.src = mediaURL

      // Only play if isPlaying is true
      if (isPlaying) {
        this.player.play().catch((error) => {
          console.error('Playback failed:', error)
        })
      }
    }
  }

  handlePlayPause = () => {
    const { isPlaying } = this.props
    if (isPlaying) {
      this.player.play().catch((error) => {
        console.error('Playback failed:', error)
      })
    } else {
      this.player.pause()
    }
  }

  timeUpdate = (e) => {
    const { isLoading, currentTime, dispatch } = this.props
    if (currentTime !== e.target.currentTime && !isLoading) {
      dispatch(PlayerActions.updateCurrentTime(e.target.currentTime))
      this.lastPosition = e.target.currentTime
    }
  }

  play = () => {
    const { isPlaying, dispatch } = this.props
    if (!isPlaying) {
      dispatch(PlayerActions.updatePlayerState(true))
      this.lastPosition = this.player?.currentTime || 0
      this.seekCount = 0
    }
  }

  pause = () => {
    const { isPlaying, dispatch, song } = this.props
    if (isPlaying) {
      dispatch(PlayerActions.updatePlayerState(false))
      this.logPlaybackStop('pause', song, this.lastPosition)
    }
  }

  ended = () => {
    const { playQueue, dispatch, autoPlay, song } = this.props

    if (!autoPlay) {
      dispatch(PlayerActions.updatePlayerState(false))
      dispatch(PlayerActions.updatePendingPlayTrigger(PLAY_TRIGGERS.REPLAY))
      this.logPlaybackStop('ended', song, this.lastPosition)
      return
    }

    const nextSong = getNextItemAndSong(playQueue).song

    if (nextSong) {
      dispatch(
        PlayQueueThunks.seekNext(playQueue, {
          source: PLAY_SOURCES.AUTOPLAY,
          trigger: PLAY_TRIGGERS.TRACK_ENDED
        })
      )
    } else {
      dispatch(PlayerActions.updatePlayerState(false))
      dispatch(PlayerActions.updatePendingPlayTrigger(PLAY_TRIGGERS.REPLAY))
      this.logPlaybackStop('ended', song, this.lastPosition)
    }
  }

  loadedData = () => {
    const { dispatch } = this.props
    dispatch(PlayerActions.updateLoadingPlayer(false))
  }

  durationChange = (e) => {
    const { dispatch } = this.props
    dispatch(PlayerActions.updateDuration(e.target.duration))
  }

  render() {
    return <audio ref={this.setRef} />
  }

  logPlaybackStop = (reason, song, positionSeconds) => {
    if (!song) return
    const durationPlayed =
      positionSeconds ||
      this.lastPosition ||
      (this.player && this.player.currentTime) ||
      0
    trackEvent('Playback Stop', {
      id: normalizeValue(song.trackid),
      artist: normalizeValue(song.artist),
      title: normalizeValue(song.title),
      duration: normalizeValue(durationPlayed),
      seek_count: normalizeValue(this.seekCount),
      reason: normalizeValue(reason),
      source: 'player',
      type: 'playback'
    })
  }
}
